import type { ReactNode } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Text,
  Badge,
} from '@fluentui/react-components';
import type { Severity, ResolutionTier } from '../api/client';
import { severityTokens } from '../theme/pillars';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalL },
  header: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalXXS },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
    boxShadow: tokens.shadow2,
  },
  centred: {
    display: 'flex',
    justifyContent: 'center',
    ...shorthands.padding(tokens.spacingVerticalXXXL),
  },
  empty: {
    color: tokens.colorNeutralForeground3,
    ...shorthands.padding(tokens.spacingVerticalXL),
    textAlign: 'center',
  },
  severity: {
    ...shorthands.padding('2px', tokens.spacingHorizontalS),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    whiteSpace: 'nowrap',
  },
});

export function PageHeader({ title, description }: { title: string; description?: string | undefined }) {
  const styles = useStyles();
  return (
    <div className={styles.header}>
      <Text as="h1" size={700} weight="semibold">
        {title}
      </Text>
      {description && (
        <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
          {description}
        </Text>
      )}
    </div>
  );
}

export function Page({ children }: { children: ReactNode }) {
  const styles = useStyles();
  return <div className={styles.page}>{children}</div>;
}

export function Card({ children }: { children: ReactNode }) {
  const styles = useStyles();
  return <section className={styles.card}>{children}</section>;
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  const styles = useStyles();
  return (
    <div className={styles.centred}>
      <Spinner label={label} />
    </div>
  );
}

export function ErrorState({ error, title = 'Something went wrong' }: { error: string; title?: string }) {
  return (
    <MessageBar intent="error">
      <MessageBarBody>
        <MessageBarTitle>{title}</MessageBarTitle>
        {/* The API's own wording, not a generic message. The consent and
            residency refusals explain themselves and that is worth showing. */}
        {error}
      </MessageBarBody>
    </MessageBar>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  const styles = useStyles();
  return <div className={styles.empty}>{children}</div>;
}

export function SeverityBadge({ severity }: { severity: Severity }) {
  const styles = useStyles();
  const { foreground, background } = severityTokens(severity);
  return (
    <span className={styles.severity} style={{ color: foreground, backgroundColor: background }}>
      {severity}
    </span>
  );
}

/**
 * Shows which tier answered a framework lookup. A control reached through the
 * global fallback is a weaker claim than one from a framework the customer
 * chose, so it is labelled rather than shown identically.
 */
export function TierBadge({ tier }: { tier: ResolutionTier }) {
  const colour = tier === 'selected' ? 'success' : tier === 'fallback' ? 'warning' : 'danger';
  const label = tier === 'selected' ? 'Selected framework' : tier === 'fallback' ? 'Fallback' : 'Unmapped';
  return (
    <Badge appearance="tint" color={colour} size="small">
      {label}
    </Badge>
  );
}
